import React, { useState, useMemo } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useCalendarEntries, type CalendarEntry } from '../../hooks/useCalendarEntries';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SOURCE_COLORS: Record<string, string> = {
  coaching_session: '#8B5CF6',
  tms_event: '#3B82F6',
  tms_session: '#10B981',
  ilt_session: '#F59E0B',
};

const SOURCE_LABELS: Record<string, string> = {
  coaching_session: 'Coaching',
  tms_event: 'Event',
  tms_session: 'Session',
  ilt_session: 'ILT',
};

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export const LearnCalendarWidget: React.FC = () => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const { entries, loading, error } = useCalendarEntries(viewYear, viewMonth);

  const entriesByDay = useMemo(() => {
    const map: Record<number, CalendarEntry[]> = {};
    entries.forEach((entry) => {
      const day = new Date(entry.start_at).getDate();
      if (!map[day]) map[day] = [];
      map[day].push(entry);
    });
    return map;
  }, [entries]);

  const sourcesByDay = useMemo(() => {
    const map: Record<number, string[]> = {};
    Object.entries(entriesByDay).forEach(([day, dayEntries]) => {
      const types: string[] = [];
      dayEntries.forEach((e) => {
        if (!types.includes(e.source_type)) types.push(e.source_type);
      });
      map[Number(day)] = types;
    });
    return map;
  }, [entriesByDay]);

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const monthLabel = new Date(viewYear, viewMonth).toLocaleString('default', { month: 'long' });

  const isToday = (day: number) =>
    viewYear === today.getFullYear() && viewMonth === today.getMonth() && day === today.getDate();

  const goToPrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const weeks = useMemo(() => {
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const result: (number | null)[][] = [];
    for (let r = 0; r < cells.length; r += 7) {
      result.push(cells.slice(r, r + 7));
    }
    return result;
  }, [firstDayOfMonth, daysInMonth]);

  return (
    <div className="flex flex-col h-full p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h3 className="text-base font-semibold text-gray-800">Calendar</h3>
        <div className="flex items-center gap-1.5">
          <button onClick={goToPrevMonth} className="p-1 hover:bg-gray-100 rounded-md transition-colors">
            <CaretLeft size={16} className="text-gray-500" />
          </button>
          <span className="text-sm font-medium text-gray-700 min-w-[110px] text-center">
            {monthLabel} {viewYear}
          </span>
          <button onClick={goToNextMonth} className="p-1 hover:bg-gray-100 rounded-md transition-colors">
            <CaretRight size={16} className="text-gray-500" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-gray-200 rounded-full animate-spin" style={{ borderTopColor: '#4B45E5' }} />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">{error}</div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 border border-gray-200 rounded-lg overflow-visible">
          {/* Day headers */}
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200 flex-shrink-0">
            {DAYS.map((d, i) => (
              <div key={i} className={`text-center text-[11px] font-semibold text-gray-500 py-1.5 ${i < 6 ? 'border-r border-gray-200' : ''}`}>
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="flex-1 grid auto-rows-fr" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {weeks.flat().map((day, idx) => {
              const ci = idx % 7;
              const ri = Math.floor(idx / 7);
              const isLastRow = ri === weeks.length - 1;
              const isLastCol = ci === 6;

              if (day === null) {
                return (
                  <div
                    key={idx}
                    className={`bg-gray-50/50 ${!isLastCol ? 'border-r border-gray-200' : ''} ${!isLastRow ? 'border-b border-gray-200' : ''}`}
                  />
                );
              }

              const dayEntries = entriesByDay[day];
              const eventTypes = sourcesByDay[day];
              const hasEvents = !!eventTypes;
              const isTodayCell = isToday(day);
              const isSunday = ci === 0;
              const isHovered = hoveredDay === day && hasEvents;

              return (
                <div
                  key={idx}
                  className={`
                    relative flex items-center justify-center cursor-default
                    ${!isLastCol ? 'border-r border-gray-200' : ''}
                    ${!isLastRow ? 'border-b border-gray-200' : ''}
                    ${isTodayCell ? 'bg-indigo-50' : hasEvents ? 'bg-white hover:bg-gray-50' : 'bg-white'}
                  `}
                  onMouseEnter={() => hasEvents && setHoveredDay(day)}
                  onMouseLeave={() => setHoveredDay(null)}
                >
                  <span
                    className={`
                      inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium
                      ${isTodayCell ? 'bg-indigo-600 text-white' : isSunday ? 'text-gray-400' : 'text-gray-700'}
                    `}
                  >
                    {day}
                  </span>

                  {/* Event count badge */}
                  {hasEvents && dayEntries && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[9px] font-semibold text-indigo-600 bg-indigo-50 rounded px-1 leading-[14px]">
                      {dayEntries.length}
                    </span>
                  )}

                  {/* Tooltip */}
                  {isHovered && dayEntries && (
                    <div
                      className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-56"
                      style={{
                        ...(ri <= 1
                          ? { top: '100%', marginTop: '2px' }
                          : { bottom: '100%', marginBottom: '2px' }
                        ),
                        left: ci <= 1 ? '0' : ci >= 5 ? 'auto' : '50%',
                        right: ci >= 5 ? '0' : 'auto',
                        transform: ci > 1 && ci < 5 ? 'translateX(-50%)' : 'none',
                      }}
                    >
                      <div className="text-xs font-semibold text-gray-500 mb-2">
                        {new Date(viewYear, viewMonth, day).toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </div>
                      <div className="space-y-2 max-h-36 overflow-auto">
                        {dayEntries.slice(0, 5).map((entry) => (
                          <div key={entry.id} className="flex items-start gap-2">
                            <div
                              className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                              style={{ backgroundColor: SOURCE_COLORS[entry.source_type] || '#6B7280' }}
                            />
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">{entry.title}</p>
                              <p className="text-[11px] text-gray-500">
                                {formatTime(entry.start_at)} – {formatTime(entry.end_at)}
                                <span className="ml-1 text-gray-400">{SOURCE_LABELS[entry.source_type] || entry.source_type}</span>
                              </p>
                            </div>
                          </div>
                        ))}
                        {dayEntries.length > 5 && (
                          <p className="text-[11px] text-gray-400">+{dayEntries.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
